<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <v-card elevation="0" class="px-4" color="transparent">
        <v-card-text>
          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="updateIngredient"
          >
            <div class="page-title">
              <h2>Edit Ingredient</h2>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                outlined
                exact
                class="mr-2"
                :to="{ name: 'IngredientInventory' }"
                >Back</v-btn
              >
              <v-btn type="submit" color="primary">Save</v-btn>
            </div>
            <div
              class="py-5 px-5"
              style="background: #ffffff; border-radius: 5px"
            >
              <v-expansion-panels focusable flat v-model="panel">
                <v-expansion-panel
                  :class="
                    isGeneralInfoFulfilled
                      ? 'information-fulfilled'
                      : 'information-not-fulfilled'
                  "
                >
                  <v-expansion-panel-header
                    class="subtitle-1"
                    :disable-icon-rotate="isGeneralInfoFulfilled"
                  >
                    <span>General Information</span>
                    <template v-slot:actions v-if="isGeneralInfoFulfilled">
                      <v-icon> mdi-pencil </v-icon>
                      <v-icon class="ml-1"> mdi-check </v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="ingredient-form">
                      <div>
                        <v-row class="pt-2">
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Ingredient Name <span class="red--text">*</span>
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.generalInformation.ingredientName"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              class="subtitle-2"
                              outlined
                              dense
                              placeholder="Ingredient Name"
                              v-model="ingredient.name"
                              :rules="ingredientRules.name"
                            >
                            </v-text-field>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Ingredient Picture
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.generalInformation.ingredientPicture"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>

                            <div v-if="!ingredient.ingredientImageUrl">
                              <div
                                :class="[
                                  'dropZone',
                                  dragging ? 'dropZone-over' : '',
                                ]"
                                @dragenter="dragging = true"
                                @dragleave="dragging = false"
                              >
                                <div
                                  class="dropZone-info"
                                  @drag="onIngredientPictureChange"
                                >
                                  <span class="dropZone-title">
                                    Drag and drop here or
                                  </span>
                                  <span
                                    class="fa fa-cloud-upload dropZone-title green--text font-weight-bold"
                                    >Browse
                                  </span>
                                  <v-icon class="green--text"
                                    >mdi-export-variant</v-icon
                                  >
                                </div>
                                <input
                                  type="file"
                                  @change="onIngredientPictureChange"
                                />
                              </div>
                            </div>
                            <div v-else class="dropZone-uploaded">
                              <div class="dropZone-uploaded-info">
                                <!-- <span class="dropZone-title">{{ animalPicture }}</span> -->
                                <img
                                  class="image-preview"
                                  :src="previewIngredientUrl"
                                />
                                <button
                                  type="button"
                                  class="btn btn-primary removeFile"
                                  @click="removeIngredientPicture"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Ingredient Type
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.generalInformation.ingredientType"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-select
                              v-model="ingredient.ingredientTypeId"
                              :items="ingredientType"
                              item-text="name"
                              item-value="id"
                              outlined
                              dense
                              :error-messages="errors.ingredientType"
                              :rules="ingredientRules.ingredientType"
                              placeholder="Select Ingredient Type"
                              :hide-details="true"
                            >
                            </v-select>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Verified Ingredient
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.generalInformation.description"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-radio-group
                              row
                              v-model="ingredient.isVerified"
                              class="mt-0"
                              :value="ingredient.isVerified"
                            >
                              <v-radio label="Verified" :value="true"></v-radio>
                              <v-radio
                                label="Unverified"
                                :value="false"
                              ></v-radio>
                            </v-radio-group>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Description
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.generalInformation.description"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-textarea
                              outlined
                              placeholder="Description"
                              v-model="ingredient.description"
                              :hide-details="true"
                            ></v-textarea>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Market Pricing
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.generalInformation.marketPricing"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-card flat color="#F3F3F380" class="pb-1">
                              <v-card-text>
                                <v-row class="mt-2">
                                  <v-col cols="3">
                                    <label class="subtitle-2"
                                      >Price
                                      <v-icon class="mb-1 pl-1">
                                        mdi-alert-circle-outline</v-icon
                                      >
                                    </label>
                                    <v-text-field
                                      type="number"
                                      class="subtitle-2 font-weight-light"
                                      outlined
                                      placeholder="0"
                                      dense
                                      @input="
                                        convertIntoUSD(
                                          ingredient.price,
                                          ingredient.currencyId
                                        )
                                      "
                                      v-model="ingredient.price"
                                      :rules="ingredientRules.price"
                                    >
                                    </v-text-field>
                                  </v-col>
                                  <v-col cols="3">
                                    <label class="subtitle-2"
                                      >Currency
                                      <v-icon class="mb-1 pl-1">
                                        mdi-alert-circle-outline</v-icon
                                      >
                                    </label>
                                    <v-autocomplete
                                      :items="currencyOptions"
                                      class="subtitle-2 font-weight-light"
                                      item-text="currency"
                                      item-value="currency"
                                      outlined
                                      dense
                                      placeholder="USD-USA-Dollar"
                                      v-model="ingredient.currencyId"
                                      :rules="ingredientRules.currency"
                                      @input="
                                        convertIntoUSD(
                                          ingredient.price,
                                          ingredient.currencyId
                                        )
                                      "
                                    ></v-autocomplete>
                                  </v-col>
                                  <v-col cols="3">
                                    <label class="subtitle-2"
                                      >Quantity
                                      <v-icon class="mb-1 pl-1">
                                        mdi-alert-circle-outline</v-icon
                                      >
                                    </label>
                                    <v-text-field
                                      type="number"
                                      class="subtitle-2 font-weight-light"
                                      outlined
                                      placeholder="1"
                                      dense
                                      v-model="ingredient.quantity"
                                      :rules="ingredientRules.quantity"
                                    >
                                    </v-text-field>
                                  </v-col>
                                  <v-col cols="3">
                                    <label class="subtitle-2"
                                      >U.O.M
                                      <v-icon class="mb-1 pl-1">
                                        mdi-alert-circle-outline</v-icon
                                      >
                                    </label>
                                    <v-select
                                      item-value="id"
                                      item-text="abbr"
                                      :items="unitOfMeasurements"
                                      class="subtitle-2 font-weight-light"
                                      outlined
                                      dense
                                      placeholder="Killometer"
                                      v-model="ingredient.unitId"
                                      :rules="ingredientRules.unitId"
                                    ></v-select>
                                  </v-col>
                                </v-row>
                              </v-card-text>
                            </v-card>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6" class="mt-3">
                            <label class="subtitle-2"
                              >Producer or Brand
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.generalInformation.producerBrand"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              class="subtitle-2 font-weight-light"
                              outlined
                              placeholder="Enter Unspecified for General Ingredient"
                              dense
                              v-model="ingredient.producerOrBrand"
                              :hide-details="true"
                            >
                            </v-text-field>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6" class="mt-3">
                            <label class="subtitle-2"
                              >Main Nutrient(s)
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.generalInformation.mainNutrientsEnergy"
                                  )
                                }}</span>
                              </v-tooltip></label
                            >
                            <div class="d-flex flex-wrap">
                              <v-checkbox
                                v-for="item in nutrients"
                                :key="item.id"
                                :value="item.id"
                                v-model="ingredient.nutrientIds"
                                class="mr-2"
                                :hide-details="true"
                              >
                                <template v-slot:label>
                                  <span :class="nutrients_color(item.name)">{{
                                    item.name
                                  }}</span>
                                </template>
                              </v-checkbox>
                            </div>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Visibility
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.generalInformation.visibility"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-select
                              v-model="ingredient.visibility"
                              :items="visibilityData"
                              item-text="text"
                              item-value="value"
                              outlined
                              dense
                              :error-messages="errors.tagType"
                              placeholder="Me"
                              :hide-details="true"
                            ></v-select>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <div>
                              <v-checkbox
                                color="#787878"
                                label="Preferred Ingredient"
                                v-model="ingredient.isPreferredIngredient"
                                class="mt-8"
                                :hide-details="true"
                              >
                              </v-checkbox>
                            </div>
                          </v-col>
                        </v-row>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel class="mt-5 information-fulfilled">
                  <v-expansion-panel-header
                    class="subtitle-1"
                    :disable-icon-rotate="isNameTranslationFulfilled"
                  >
                    <span>Name Translation</span>
                    <template v-slot:actions v-if="isNameTranslationFulfilled">
                      <v-icon> mdi-pencil </v-icon>
                      <v-icon class="ml-1"> mdi-check </v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div>
                      <v-row
                        v-for="(item, index) in ingredient.translation"
                        :key="index"
                        class="pt-4"
                      >
                        <v-col lg="6" md="6" sm="6" xs="6">
                          <label class="subtitle-2"
                            >Ingredient Name
                            <v-tooltip top color="black" max-width="350">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">
                                  mdi-alert-circle-outline
                                </v-icon>
                              </template>
                              <span>{{
                                $t(
                                  "ingredientInventory.nameTranslation.ingredientName"
                                )
                              }}</span>
                            </v-tooltip>
                          </label>
                          <v-text-field
                            type="text"
                            outlined
                            dense
                            placeholder="Ingredient Name"
                            v-model="item.name"
                          >
                          </v-text-field>
                        </v-col>
                        <v-col lg="4" md="4" sm="4" xs="4">
                          <label class="subtitle-2"
                            >Language
                            <v-tooltip top color="black" max-width="350">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">
                                  mdi-alert-circle-outline
                                </v-icon>
                              </template>
                              <span>{{
                                $t(
                                  "ingredientInventory.nameTranslation.language"
                                )
                              }}</span>
                            </v-tooltip>
                          </label>
                          <v-select
                            outlined
                            dense
                            placeholder="Select Language"
                            item-value="id"
                            item-text="name"
                            :items="languages"
                            v-model="item.languageId"
                          ></v-select>
                        </v-col>
                        <v-col lg="2" md="2" sm="2" xs="2">
                          <v-btn
                            icon
                            color="#FF0000"
                            @click="removeNameTranslationArr(index)"
                            v-if="index < ingredient.translation.length - 1"
                            class="mt-7"
                          >
                            <v-icon>mdi-trash-can-outline</v-icon>
                          </v-btn>
                          <v-btn
                            @click="addOnNameTranslationArr"
                            color="primary"
                            class="mt-7"
                            v-else
                          >
                            <v-icon>mdi-plus</v-icon>Add
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <v-expansion-panel
                  class="mt-5"
                  :class="
                    isTechnicalInfoFulfilled ? 'technical-info-fulfilled' : ''
                  "
                >
                  <v-expansion-panel-header
                    class="subtitle-1"
                    :disable-icon-rotate="isTechnicalInfoFulfilled"
                  >
                    <span>Technical Info</span>
                    <template v-slot:actions v-if="isTechnicalInfoFulfilled">
                      <v-icon> mdi-pencil </v-icon>
                      <v-icon class="ml-1"> mdi-check </v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div>
                      <v-row
                        v-for="(item, index) in ingredient.technicalInfo"
                        :key="index"
                        class="pt-4"
                      >
                        <v-col lg="6" md="6" sm="12" xs="12">
                          <label class="subtitle-2"
                            >Scientific Name
                            <v-tooltip top color="black" max-width="350">
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on">
                                  mdi-alert-circle-outline
                                </v-icon>
                              </template>
                              <span>{{
                                $t(
                                  "ingredientInventory.technicalInfo.scientificName"
                                )
                              }}</span>
                            </v-tooltip>
                          </label>
                          <v-text-field
                            type="text"
                            outlined
                            dense
                            placeholder="Scientific Name"
                            v-model="item.scientificName"
                          >
                          </v-text-field>
                        </v-col>
                        <!-- <v-col lg="6" md="6" sm="6" xs="6">
                          <label class="subtitle-2">Lab Result </label>
                          <div v-if="!item.lab_result">
                            <div
                              :class="[
                                'dropZone',
                                dragging ? 'dropZone-over' : '',
                              ]"
                              @dragenter="dragging = true"
                              @dragleave="dragging = false"
                            >
                              <div
                                class="dropZone-info"
                                @drag="onLabResultPictureChange($event, index)"
                              >
                                <span class="dropZone-title">
                                  Drag and drop here
                                </span>
                                <span
                                  class="fa fa-cloud-upload dropZone-title green--text font-weight-bold"
                                  >Browse
                                </span>
                                <v-icon class="green--text"
                                  >mdi-export-variant</v-icon
                                >
                              </div>
                              <input
                                type="file"
                                @change="
                                  onLabResultPictureChange($event, index)
                                "
                              />
                            </div>
                          </div>
                          <div v-else class="dropZone-uploaded">
                            <div class="dropZone-uploaded-info">
                              <span class="dropZone-title">{{ animalPicture }}</span>
                              <img
                                class="image-preview"
                                :src="item.previewLabResultUrl"
                              />
                              <button
                                type="button"
                                class="btn btn-primary removeFile"
                                @click="removeLabResultPicture(index)"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </v-col> -->
                        <v-col lg="6" md="6" sm="12" xs="12">
                          <label class="subtitle-2"
                            >Suppliers
                            <v-icon class="mb-1">
                              mdi-alert-circle-outline</v-icon
                            >
                          </label>
                          <v-select
                            v-model="item.suppliers"
                            :items="suppliers"
                            item-text="supplierName"
                            item-value="id"
                            outlined
                            dense
                            placeholder="Suppliers"
                          ></v-select>
                        </v-col>
                        <v-col lg="6" md="6" sm="12" xs="12">
                          <div>
                            <label class="subtitle-2"
                              >Geographic Origin
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.technicalInfo.geographicOrigin"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-select
                              :items="geoGraphicOrigin"
                              item-value="id"
                              item-text="name"
                              outlined
                              dense
                              placeholder="Uganda"
                              v-model="item.graphicOriginId"
                            ></v-select>
                          </div>
                        </v-col>
                        <v-col lg="2" md="2" sm="2" xs="2">
                          <v-btn
                            icon
                            color="#FF0000"
                            @click="removeTechnicalInfoArr(index)"
                            v-if="index < ingredient.technicalInfo.length - 1"
                            class="mt-7"
                          >
                            <v-icon>mdi-trash-can-outline</v-icon>
                          </v-btn>
                          <v-btn
                            @click="addOnTechnicalInfoArr"
                            color="primary"
                            class="mt-7"
                            v-else
                          >
                            <v-icon>mdi-plus</v-icon>Add
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel
                  class="mt-5"
                  :class="
                    isNutritionalValueFulfilled
                      ? 'nutritional-value-fulfilled'
                      : 'nutritional-value-not-fulfilled'
                  "
                >
                  <v-expansion-panel-header
                    class="subtitle-1"
                    :disable-icon-rotate="isNutritionalValueFulfilled"
                  >
                    <span>Nutritional Values</span>
                    <template v-slot:actions v-if="isNutritionalValueFulfilled">
                      <v-icon> mdi-pencil </v-icon>
                      <v-icon class="ml-1"> mdi-check </v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="animal-from">
                      <div class="pt-2">
                        <h3 class="pb-3">Main Nutritional Value</h3>
                        <hr />
                        <div class="pt-4 pb-1">
                          Required: CF or both ADF and NDF
                          <v-icon class="mb-1">mdi-alert-circle-outline</v-icon>
                        </div>
                        <v-card>
                          <v-card-text>
                            <v-row>
                              <v-col lg="4" md="4" sm="6" xs="6">
                                <div class="ml-2">
                                  <label class="subtitle-2"
                                    >Crude Fiber (CF)%
                                    <v-tooltip
                                      top
                                      color="black"
                                      max-width="350"
                                    >
                                      <template
                                        v-slot:activator="{ on, attrs }"
                                      >
                                        <v-icon v-bind="attrs" v-on="on">
                                          mdi-alert-circle-outline
                                        </v-icon>
                                      </template>
                                      <span>{{
                                        $t(
                                          "ingredientInventory.mainNutritionalValues.crudeFiber"
                                        )
                                      }}</span>
                                    </v-tooltip>
                                  </label>
                                  <v-text-field
                                    type="text"
                                    solo
                                    dense
                                    flat
                                    background-color="#F3F3F3"
                                    v-model="
                                      ingredient.nutritionalValues.crudeFiber
                                    "
                                  ></v-text-field>
                                </div>
                              </v-col>
                              <v-col lg="4" md="4" sm="6" xs="6">
                                <div>
                                  <label class="subtitle-2"
                                    >Acid Detergent Fiber (ADF)%
                                    <v-tooltip
                                      top
                                      color="black"
                                      max-width="350"
                                    >
                                      <template
                                        v-slot:activator="{ on, attrs }"
                                      >
                                        <v-icon v-bind="attrs" v-on="on">
                                          mdi-alert-circle-outline
                                        </v-icon>
                                      </template>
                                      <span>{{
                                        $t(
                                          "ingredientInventory.mainNutritionalValues.acidDetergentFiber"
                                        )
                                      }}</span>
                                    </v-tooltip>
                                  </label>
                                  <v-text-field
                                    type="text"
                                    solo
                                    dense
                                    flat
                                    background-color="#F3F3F3"
                                    v-model="
                                      ingredient.nutritionalValues
                                        .acidDetergentFiber
                                    "
                                  ></v-text-field>
                                </div>
                              </v-col>
                              <v-col lg="4" md="4" sm="6" xs="6">
                                <div class="mr-2">
                                  <label class="subtitle-2"
                                    >Neutral Detergent Fiber (NDF)%
                                    <v-tooltip
                                      top
                                      color="black"
                                      max-width="350"
                                    >
                                      <template
                                        v-slot:activator="{ on, attrs }"
                                      >
                                        <v-icon v-bind="attrs" v-on="on">
                                          mdi-alert-circle-outline
                                        </v-icon>
                                      </template>
                                      <span>{{
                                        $t(
                                          "ingredientInventory.mainNutritionalValues.neutralDetergentFiber"
                                        )
                                      }}</span>
                                    </v-tooltip>
                                  </label>
                                  <v-text-field
                                    type="text"
                                    solo
                                    dense
                                    flat
                                    background-color="#F3F3F3"
                                    v-model="
                                      ingredient.nutritionalValues
                                        .neutralDetergentFiber
                                    "
                                  ></v-text-field>
                                </div>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                        <v-row class="pt-6">
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Crude Protein(CP)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.mainNutritionalValues.crudeProtein"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="
                                ingredient.nutritionalValues.crudeProtein
                              "
                              type="text"
                              outlined
                              dense
                              :rules="ingredientRules.CP"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Dry Matter(DM)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.mainNutritionalValues.dryMatter"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.dryMatter"
                              type="text"
                              outlined
                              dense
                              :rules="ingredientRules.DM"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Ether Extract (EE)/Crude Fiber (%)
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.mainNutritionalValues.etherExtract"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="
                                ingredient.nutritionalValues.etherExtract
                              "
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Gross Energy (GE)MJ/Kg
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.mainNutritionalValues.grossEnergy"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.grossEnergy"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Total Digestible Nutrients (TDN) %
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.mainNutritionalValues.totalDigestibleNutrients"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.tdn"
                              type="text"
                              outlined
                              dense
                              :rules="ingredientRules.TDN"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <h2 class="pb-3">Minerals</h2>
                            <hr />
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Calcium(Ca)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.minerals.calcium")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.calcium"
                              type="text"
                              outlined
                              dense
                              :rules="ingredientRules.CA"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Phosphorus (P)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.minerals.phosphorus")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.phosphorus"
                              type="text"
                              outlined
                              dense
                              :rules="ingredientRules.P"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Magnesium (Mg)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.minerals.magnesium")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.magnesium"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Potassium (K)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.minerals.potassium")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.potassium"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Sulfur (S)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.minerals.sulfur")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.sulfur"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Sodium (Na)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.minerals.sodium")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.sodium"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Zinc (Zn)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.minerals.zinc")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.zinc"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Chlorine (Cl)%
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.minerals.chlorine")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.chlorine"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <h2 class="pb-3">Vitamins</h2>
                            <hr />
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Vit A Value (Vit Ae Carotene) Ul
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.vitamins.vitAValue")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.vitA"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Vit D UI
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.vitamins.vitDValue")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.vitD"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Vit E UI
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t("ingredientInventory.vitamins.vitEValue")
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              v-model="ingredient.nutritionalValues.vitE"
                              type="text"
                              outlined
                              dense
                              :hide-details="true"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col cols="12">
                            <h2 class="pb-3">Energy Per Species</h2>
                            <hr />
                          </v-col>
                        </v-row>
                        <v-row
                          v-for="(item, index) in ingredient.nutritionalValues
                            .energyPerSpecies"
                          :key="index"
                        >
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Species
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.energyPerSpecies.species"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-select
                              :items="species"
                              item-value="id"
                              item-text="name"
                              outlined
                              dense
                              placeholder="Select Species"
                              v-model="item.speciesId"
                            ></v-select>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Digestible energy (DE) MJ/Kg
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.energyPerSpecies.digestibleEnergyMJKG"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              type="text"
                              outlined
                              dense
                              v-model="item.digestibleEnergy"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="3" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Metabolizable Energy (ME) MJ/Kg
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.energyPerSpecies.metabolizableEnergyMJKg"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              type="text"
                              outlined
                              dense
                              v-model="item.metabolizableEnergy"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="2" md="4" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Net Energy (NE) MJ/Kg
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.energyPerSpecies.netEnergyMJKg"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              type="text"
                              outlined
                              dense
                              v-model="item.netEnergy"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="1" md="4" sm="6" xs="6">
                            <v-btn
                              icon
                              color="#FF0000"
                              @click="removeEnergyPerSpeciesArr(index)"
                              v-if="
                                index <
                                ingredient.nutritionalValues.energyPerSpecies
                                  .length -
                                  1
                              "
                              class="mt-7"
                            >
                              <v-icon>mdi-trash-can-outline</v-icon>
                            </v-btn>
                            <v-btn
                              @click="addOnEnergyPerSpeciesArr"
                              color="primary"
                              class="mt-7"
                              v-else
                            >
                              <v-icon>mdi-plus</v-icon>Add
                            </v-btn>
                          </v-col>
                        </v-row>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel
                  class="mt-5"
                  :class="
                    isSafetyRestrictionFulfilled
                      ? 'safety-restriction-fulfilled'
                      : ''
                  "
                >
                  <v-expansion-panel-header
                    class="subtitle-1"
                    :disable-icon-rotate="isSafetyRestrictionFulfilled"
                  >
                    <span>Safety Restrictions</span>
                    <template
                      v-slot:actions
                      v-if="isSafetyRestrictionFulfilled"
                    >
                      <v-icon> mdi-pencil </v-icon>
                      <v-icon class="ml-1"> mdi-check </v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="animal-from">
                      <div>
                        <v-row class="pt-2">
                          <v-col lg="6" md="6" sm="12" xs="12">
                            <label class="subtitle-2"
                              >Proper Usage Instructions And Important
                              Recommendations
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.safetyRestrictions.properUsageInstructionsAndImportantRecommendations"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-textarea
                              outlined
                              placeholder="Description"
                              v-model="
                                ingredient.safetyRestrictions.usageInstrictions
                              "
                            ></v-textarea>
                          </v-col>
                          <v-col lg="6" md="6" sm="12" xs="12">
                            <label class="subtitle-2"
                              >Risk Warnings And Feeding Instructions:
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.safetyRestrictions.riskWarningsAndFeedingInstructions"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-textarea
                              outlined
                              placeholder="Description"
                              v-model="
                                ingredient.safetyRestrictions.riskWarning
                              "
                            ></v-textarea>
                          </v-col>
                        </v-row>
                        <v-row class="px-3">
                          <v-col
                            class="px-4 subtitle-2"
                            cols="3"
                            v-for="item in safetyDataHeader"
                            :key="item.value"
                            style="background-color: #ffb443"
                          >
                            {{ item.text }}
                          </v-col>
                        </v-row>
                        <v-row
                          v-for="(item, index) in ingredient.safetyRestrictions
                            .speciesSafety"
                          :key="index"
                        >
                          <v-col cols="1">
                            <v-btn
                              icon
                              color="#FF0000"
                              @click="removeItemSafetyInformation(index)"
                              v-if="
                                index <
                                ingredient.safetyRestrictions.speciesSafety
                                  .length -
                                  1
                              "
                            >
                              <v-icon>mdi-trash-can-outline</v-icon>
                            </v-btn>
                          </v-col>
                          <v-col cols="2">
                            <v-select
                              outlined
                              dense
                              placeholder="Select"
                              :items="species"
                              v-model="item.speciesId"
                              item-value="id"
                              item-text="name"
                            ></v-select>
                          </v-col>
                          <v-col cols="3">
                            <v-text-field
                              type="number"
                              outlined
                              dense
                              placeholder="e.e. (123.1.5, 1000)"
                              v-model="item.minInclusionRatio"
                            >
                            </v-text-field>
                          </v-col>
                          <v-col cols="3">
                            <v-text-field
                              type="number"
                              outlined
                              dense
                              placeholder="e.e. (123.1.5, 1000)"
                              v-model="item.maxInclusionRatio"
                            >
                            </v-text-field>
                          </v-col>
                          <v-col cols="3">
                            <v-btn
                              @click="addOnSafetyInformation"
                              color="primary"
                              v-if="
                                index >=
                                ingredient.safetyRestrictions.speciesSafety
                                  .length -
                                  1
                              "
                            >
                              <v-icon>mdi-plus</v-icon>Add
                            </v-btn>
                          </v-col>
                        </v-row>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel
                  class="mt-5"
                  :class="
                    isAuthPermissionFulfilled ? 'auth-permission-fulfilled' : ''
                  "
                >
                  <v-expansion-panel-header
                    class="subtitle-1"
                    :disable-icon-rotate="isAuthPermissionFulfilled"
                  >
                    <span>Authentication/Permission</span>
                    <template v-slot:actions v-if="isAuthPermissionFulfilled">
                      <v-icon> mdi-pencil </v-icon>
                      <v-icon class="ml-1"> mdi-check </v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="animal-from">
                      <div>
                        <v-row class="pt-2">
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Provider
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.authenticationPermission.provider"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              type="text"
                              outlined
                              dense
                              placeholder="User name of NAGRC"
                              v-model="ingredient.authentication.provider"
                            ></v-text-field>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Date
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.authenticationPermission.date"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>

                            <v-menu
                              v-model="dateModel"
                              :close-on-content-click="false"
                              max-width="290"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  clearable
                                  readonly
                                  :value="ingredient.authentication.date"
                                  outlined
                                  dense
                                  v-bind="attrs"
                                  v-on="on"
                                  @click:clear="
                                    ingredient.authentication.date = null
                                  "
                                  placeholder="12/10/2021"
                                >
                                </v-text-field>
                              </template>
                              <v-date-picker
                                v-model="ingredient.authentication.date"
                                @input="dateModel = false"
                              ></v-date-picker>
                            </v-menu>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >First Name of Supplier
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.authenticationPermission.firstNameOfSupplier"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              type="text"
                              outlined
                              dense
                              placeholder="Name of Data Base"
                              v-model="ingredient.authentication.firstName"
                            >
                            </v-text-field>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Last Name of Supplier
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.authenticationPermission.lastNameOfSupplier"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              type="text"
                              outlined
                              dense
                              placeholder="Last Name of Data Base"
                              v-model="ingredient.authentication.lastName"
                            >
                            </v-text-field>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Farm Info Country
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.authenticationPermission.farmInfoCountry"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              type="text"
                              outlined
                              dense
                              placeholder="Info Farm Country of Data base"
                              v-model="
                                ingredient.authentication.farmInfoCountry
                              "
                            >
                            </v-text-field>
                          </v-col>
                          <v-col lg="6" md="6" sm="6" xs="6">
                            <label class="subtitle-2"
                              >Farm Province or Region
                              <v-tooltip top color="black" max-width="350">
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon v-bind="attrs" v-on="on">
                                    mdi-alert-circle-outline
                                  </v-icon>
                                </template>
                                <span>{{
                                  $t(
                                    "ingredientInventory.authenticationPermission.farmProvinceOrRegion"
                                  )
                                }}</span>
                              </v-tooltip>
                            </label>
                            <v-text-field
                              type="text"
                              outlined
                              dense
                              placeholder="Info farm Region of Data Base"
                              v-model="ingredient.authentication.farmProvince"
                            >
                            </v-text-field>
                          </v-col>
                        </v-row>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import loading from "@/mixins/LoadingMixin";
import { ingredientRules } from "@/validations/IngredientRules.js";
import gql from "graphql-tag";
import { generalInfo } from "@/graphql/IngredientInventory.gql";
import S3UploadServce from "@/_services/S3UploadService";
import axios from "axios";

export default {
  apollo: {
    ingredientToUpdate: {
      query: generalInfo,
      variables() {
        return { id: parseInt(this.$route.params.ingredient_number) };
      },
      update: data => data.getIngredientById,
      result({ data }) {
        let ingredient = data.getIngredientById;

        // GENERAL INFO
        if (ingredient.ingredientTypeId) {
          ingredient.ingredientTypeId = ingredient.ingredientTypeId.toString();
        }

        if (ingredient.unitId) {
          ingredient.unitId = ingredient.unitId.toString();
        }

        ingredient.converted_usd_price =
          data.getIngredientById.converted_usd_price ?? 0;

        // NAME TRANSLATION
        if (ingredient.translation.length <= 0) {
          ingredient.translation = [
            {
              languageId: "",
              name: "",
            },
          ];
        } else {
          const newTranslation = ingredient.translation.map(item => {
            if (item.languageId) {
              item.languageId = item.languageId.toString();
            }
            return {
              languageId: item.languageId,
              name: item.name,
            };
          });
          ingredient.translation = newTranslation;
        }

        // TECHNICAL INFO
        if (ingredient.technicalInfo.length <= 0) {
          ingredient.technicalInfo = [
            {
              graphicOriginId: "",
              scientificName: "",
              suppliers: "",
              lab_result: "",
              previewLabResultUrl: null,
            },
          ];
        } else {
          const newTechnicalInfo = ingredient.technicalInfo.map(item => {
            if (item.graphicOriginId) {
              item.graphicOriginId = item.graphicOriginId.toString();
            }
            return {
              graphicOriginId: item.graphicOriginId,
              scientificName: item.scientificName,
              suppliers: item.suppliers,
              lab_result: item.lab_result,
              previewLabResultUrl: item.lab_result,
            };
          });
          ingredient.technicalInfo = newTechnicalInfo;
        }

        // NUTRITION VALUE
        if (ingredient.nutritionalValues == null) {
          ingredient.nutritionalValues = {
            ...ingredient.nutritionalValues,
            energyPerSpecies: [
              {
                speciesId: "",
                digestibleEnergy: "",
                metabolizableEnergy: "",
                netEnergy: "",
              },
            ],
          };
        } else {
          var newEnergyPerSpecies = [
            {
              speciesId: "",
              digestibleEnergy: "",
              metabolizableEnergy: "",
              netEnergy: "",
            },
          ];

          if (
            ingredient.nutritionalValues.energyPerSpecies &&
            ingredient.nutritionalValues.energyPerSpecies.length > 0
          ) {
            newEnergyPerSpecies =
              ingredient.nutritionalValues.energyPerSpecies.map(item => {
                if (item.speciesId) {
                  item.speciesId = item.speciesId.toString();
                }
                return {
                  speciesId: item.speciesId,
                  digestibleEnergy: item.digestibleEnergy,
                  metabolizableEnergy: item.metabolizableEnergy,
                  netEnergy: item.netEnergy,
                };
              });
          }
          ingredient.nutritionalValues = {
            ...ingredient.nutritionalValues,
            energyPerSpecies: newEnergyPerSpecies,
          };
        }

        // SAFETY RESTRICTION
        if (ingredient.safetyRestrictions == null) {
          ingredient.safetyRestrictions = {
            riskWarning: "",
            usageInstrictions: "",
            speciesSafety: [
              {
                speciesId: "",
                minInclusionRatio: "",
                maxInclusionRatio: "",
              },
            ],
          };
        } else {
          var newSafetyRestriction = [
            {
              speciesId: "",
              minInclusionRatio: "",
              maxInclusionRatio: "",
            },
          ];
          if (
            ingredient.safetyRestrictions.speciesSafety &&
            ingredient.safetyRestrictions.speciesSafety.length > 0
          ) {
            newSafetyRestriction =
              ingredient.safetyRestrictions.speciesSafety.map(item => {
                if (item.speciesId) {
                  item.speciesId = item.speciesId.toString();
                }
                return {
                  speciesId: item.speciesId,
                  minInclusionRatio: item.minInclusionRatio,
                  maxInclusionRatio: item.maxInclusionRatio,
                };
              });
          }

          ingredient.safetyRestrictions = {
            riskWarning: ingredient.safetyRestrictions.riskWarning,
            usageInstrictions: ingredient.safetyRestrictions.usageInstrictions,
            speciesSafety: newSafetyRestriction,
          };
        }

        // AUTHENTICATION & PERMISSIONS
        if (ingredient.authentication == null) {
          ingredient.authentication = {
            provider: "",
            date: null,
            firstName: "",
            lastName: "",
            farmInfoCountry: "",
            farmProvince: "",
          };
        } else {
          ingredient.authentication = {
            provider: ingredient.authentication.provider,
            date: new Date(ingredient.authentication.date)
              .toISOString()
              .slice(0, 10),
            firstName: ingredient.authentication.firstName,
            lastName: ingredient.authentication.lastName,
            farmInfoCountry: ingredient.authentication.farmInfoCountry,
            farmProvince: ingredient.authentication.farmProvince,
          };
        }
        this.ingredient = ingredient;
        this.ingredient.nutrientIds = ingredient.nutrientIds.map(item =>
          item.nutrientId.toString()
        );
      },
    },
    getDropDownIngredientGeneralInfo: {
      query: gql`
        query ingredientType {
          getDropDownDataIngredientGeneralInfo {
            ingredientType {
              id
              name
            }
            nutrients {
              id
              name
            }
            unitOfMeasurements {
              id
              abbr
            }
            languages {
              id
              name
              abbr
            }
            graphicsOrigin {
              id
              name
            }
          }
        }
      `,
      fetchPolicy: "network-only",
      result({ data }) {
        this.ingredientType =
          data.getDropDownDataIngredientGeneralInfo.ingredientType;
        this.unitOfMeasurements =
          data.getDropDownDataIngredientGeneralInfo.unitOfMeasurements;
        this.languages = data.getDropDownDataIngredientGeneralInfo.languages;
        this.geoGraphicOrigin =
          data.getDropDownDataIngredientGeneralInfo.graphicsOrigin;
        this.nutrients = data.getDropDownDataIngredientGeneralInfo.nutrients;
      },
    },
    species: {
      query: gql`
        query species {
          species {
            id
            name
          }
        }
      `,
    },
    suppliers: {
      query: gql`
        query suppliers {
          getSuppliers {
            id
            supplierName
          }
        }
      `,
      fetchPolicy: "network-only",

      result({ data }) {
        this.suppliers = data.getSuppliers;
      },
    },
  },
  async mounted() {
    this.ingredient.farmId = parseInt(this.$store.state.farm.farm.id);
    const url = "https://www.floatrates.com/daily/usd.json";
    const res = await axios.get(url, {
      transformRequest: (data, headers) => {
        delete headers.common["authorization"];
        delete headers.common["oauth-token"];
        return data;
      },
    });
    const exchangeRate = res.data;
    Object.keys(exchangeRate).forEach(key => {
      const temp = {
        type: key,
        currency: exchangeRate[key].alphaCode,
        rate: exchangeRate[key].rate,
      };
      this.currencyOptions.push(temp);
    });
  },
  computed: {
    isGeneralInfoFulfilled() {
      return (
        this.ingredient.name &&
        this.ingredient.price &&
        this.ingredient.currencyId &&
        this.ingredient.unitId
      );
    },

    isNameTranslationFulfilled() {
      const translationArr = this.ingredient.translation;
      return translationArr.length &&
        translationArr.some(
          translation => translation.name || translation.languageId
        )
        ? true
        : false;
    },

    isTechnicalInfoFulfilled() {
      const technicalInfoArr = this.ingredient.technicalInfo;
      return technicalInfoArr.length &&
        technicalInfoArr.some(
          info => info.scientificName || info.graphicOriginId || info.suppliers
        )
        ? true
        : false;
    },

    isNutritionalValueFulfilled() {
      const nutritionalValues = this.ingredient.nutritionalValues;
      if (
        nutritionalValues.crudeProtein &&
        nutritionalValues.dryMatter &&
        nutritionalValues.tdn &&
        nutritionalValues.calcium &&
        nutritionalValues.phosphorus
      ) {
        return true;
      } else {
        return false;
      }
    },
    isSafetyRestrictionFulfilled() {
      const safetyRestrictions = this.ingredient.safetyRestrictions;
      const speciesSafety = this.ingredient.safetyRestrictions.speciesSafety;
      if (
        safetyRestrictions.usageInstrictions ||
        safetyRestrictions.riskWarning
      ) {
        return true;
      }
      if (speciesSafety.length) {
        if (
          speciesSafety.some(
            species =>
              species.speciesId ||
              species.minInclusionRatio ||
              species.maxInclusionRatio
          )
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },

    isAuthPermissionFulfilled() {
      return (
        this.ingredient.authentication.provider ||
        this.ingredient.authentication.date ||
        this.ingredient.authentication.supplierFirstName ||
        this.ingredient.authentication.supplierLastName ||
        this.ingredient.authentication.country ||
        this.ingredient.authentication.region
      );
    },
  },

  data() {
    return {
      ingredientType: [],
      currencyOptions: [
        {
          type: "usd",
          currency: "USD",
          rate: 1,
        },
      ],
      unitOfMeasurements: [],
      visibilityData: ["Me", "Public"],
      languages: [],
      geoGraphicOrigin: [],
      nutrients: [],
      dateModel: false,

      showPrimaryGeofenceDialog: false,
      showCreateGroupDialog: true,
      showCreateGroupFromExistingDialog: true,
      safetyDataHeader: [
        {
          text: "Species",
          value: "species",
          class: "black--text",
        },
        {
          text: "Min. Inclusion Ratio",
          value: "minInclusionRatio",
          class: "black--text",
        },
        {
          text: "Max. Inclusion Ratio",
          value: "maxInclusionRatio",
          class: "black--text",
        },
        {
          text: "Action",
          value: "action",
          class: "black--text",
        },
      ],
      ingredientRules,
      panel: 0,
      valid: true,
      disableFarm: false,
      imageRules: [
        value =>
          !value ||
          value.size < 1000000 ||
          "Avatar size should be less than 1 MB!",
      ],
      breadcrumbs: [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t("Ingredient Inventory"),
          to: {
            name: "IngredientInventory",
          },
          exact: true,
        },
        {
          text: "Edit Ingredient",
          disabled: true,
        },
      ],
      openRequired: false,
      errors: {
        animalImg: "",
        tagType: "",
        tagNumber: "",
        registrationNumber: "",
        birthWeight: "",
        birthEase: "",
        birthFarmId: "",
        ownerName: "",
        sirePubId: "",
        damPubId: "",
        surrogateDamPubId: "",
        breed: "",
        conceptionMethod: "",
        gender: "",
        dob: "",
        operator: "",
        animalRemovedFarmId: "",
        animalRemovalDate: "",
        removedAnimalDestination: "",
        animalRemovalReason: "",
        name: "",
        trackingNumber: "",
        brand: "",
        tattoo: "",
        hornStatus: "",
        origin: "",
        previousOwnerName: "",
        twin: "",
        breedAssociationName: "",
        breedAssociationRegNo: "",
        breeder: "",
        note: "",
      },

      today: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      previewIngredientUrl: null,
      dragging: false,
      ingredient: {
        name: "",
        ingredientImageUrl: "",
        ingredientTypeId: "",
        isVerified: null,
        description: "",
        price: "",
        converted_usd_price: 0,
        currencyId: "",
        quantity: "",
        unitId: "",
        farmId: "",
        producerOrBrand: "",
        mainNutrients: [],
        suppliers: "",
        visibility: "",
        isPreferredIngredient: false,
        nutrientIds: [],

        translation: [
          {
            name: "",
            languageId: "",
          },
        ],
        technicalInfo: [
          {
            scientificName: "",
            graphicOriginId: "",
            suppliers: "",
            previewLabResultUrl: null,
            lab_result: "",
          },
        ],
        nutritionalValues: {
          crudeFiber: "",
          acidDetergentFiber: "",
          neutralDetergentFiber: "",
          crudeProtein: "",
          dryMatter: "",
          etherExtract: "",
          grossEnergy: "",
          tdn: "",
          calcium: "",
          phosphorus: "",
          magnesium: "",
          potassium: "",
          sulfur: "",
          sodium: "",
          zinc: "",
          chlorine: "",
          vitA: "",
          vitD: "",
          vitE: "",
          energyPerSpecies: [
            {
              speciesId: "",
              digestibleEnergy: "",
              metabolizableEnergy: "",
              netEnergy: "",
            },
          ],
        },

        safetyRestrictions: {
          usageInstrictions: "",
          riskWarning: "",
          speciesSafety: [
            {
              speciesId: "",
              minInclusionRatio: "",
              maxInclusionRatio: "",
            },
          ],
        },

        authentication: {
          provider: "",
          date: "",
          firstName: "",
          lastName: "",
          farmInfoCountry: "",
          farmProvince: "",
        },
      },
    };
  },
  methods: {
    async updateIngredient() {
      if (!this.$refs.form.validate()) return;
      this.startLoading();

      let formData = new FormData();

      // GENERAL INFORMATION
      let uploadedIngredientImage;
      if (
        this.ingredient.ingredientImageUrl &&
        typeof this.ingredient.ingredientImageUrl == "object"
      ) {
        //file and folder to aws
        uploadedIngredientImage = await S3UploadServce.singleUpload(
          this.ingredient.ingredientImageUrl,
          "ingredient_inventory"
        );
        this.ingredient.ingredientImageUrl = uploadedIngredientImage.fullPath;
      }

      formData.id = parseInt(this.ingredient.id);
      formData.name = this.ingredient.name;
      formData.ingredientImageUrl = this.ingredient.ingredientImageUrl;
      formData.description = this.ingredient.description;
      formData.isVerified = this.ingredient.isVerified;
      formData.ingredientTypeId = parseInt(this.ingredient.ingredientTypeId);
      formData.price = parseInt(this.ingredient.price);
      formData.converted_usd_price = parseInt(
        this.ingredient.converted_usd_price
      );
      formData.currencyId = this.ingredient.currencyId;
      formData.quantity = parseInt(this.ingredient.quantity);
      formData.unitId = parseInt(this.ingredient.unitId);
      formData.isPreferredIngredient = this.ingredient.isPreferredIngredient;
      formData.userId = this.ingredient.userId;
      formData.producerOrBrand = this.ingredient.producerOrBrand;
      formData.nutrientIds = this.ingredient.nutrientIds.map(id =>
        parseInt(id)
      );
      formData.suppliers = this.ingredient.suppliers;
      formData.visibility = this.ingredient.visibility;
      formData.farmId = this.ingredient.farmId;

      // NAME TRANSLATION

      formData.nameTranslations = this.ingredient.translation.map(
        translation => {
          return {
            ...translation,
            languageId: parseInt(translation.languageId),
          };
        }
      );

      //TECHNICAL INFO

      formData.technicalInfo = this.ingredient.technicalInfo.map(techInfo => {
        return {
          scientificName: techInfo.scientificName,
          suppliers: techInfo.suppliers,
          // lab_result: techInfo.lab_result,
          graphicOriginId: parseInt(techInfo.graphicOriginId),
        };
      });

      // NUTRITIONAL VALUES
      let nutritionalValues = this.ingredient.nutritionalValues;
      delete nutritionalValues.id;
      delete nutritionalValues.__typename;

      const energyPerSpecies = nutritionalValues.energyPerSpecies.map(eps => {
        return {
          ...eps,
          speciesId: parseInt(eps.speciesId),
        };
      });
      nutritionalValues.energyPerSpecies = energyPerSpecies;

      formData.nutritionalValues = nutritionalValues;

      // SAFETY RESTRICTIONS

      let safetyRestrictions = this.ingredient.safetyRestrictions;
      const speciesSafety = safetyRestrictions.speciesSafety.map(safety => {
        return {
          ...safety,
          speciesId: parseInt(safety.speciesId),
        };
      });
      safetyRestrictions.speciesSafety = speciesSafety;
      formData.safetyRestrictions = safetyRestrictions;

      // // AUTHENTICATION/PERMISSIONS

      formData.authentication = this.ingredient.authentication;

      this.$apollo
        .mutate({
          // Query
          mutation: gql`
            mutation ($updateIngredientInput: UpdateIngredientInput!) {
              updateIngredient(updateIngredientInput: $updateIngredientInput) {
                id
                name
              }
            }
          `,
          variables: {
            updateIngredientInput: formData,
          },
        })
        .then(response => {
          if (response.data.updateIngredient) {
            console.log("resonde", response.data);

            this.$notify({
              title: "Ingredient Updated Successfully",
              text: "", //res.message,
              type: "success",
            });
            this.stopLoading();

            this.$router.push({ name: "IngredientInventory" });
          }
        })
        .catch(err => {
          console.log("catch error", err);
          this.$notify({
            title: "Ingredient Not Updated",
            text: "",
            type: "error",
          });
          this.stopLoading();
        });
    },

    addOnNameTranslationArr() {
      this.ingredient.translation.push({
        name: "",
        languageId: "",
      });
    },

    removeNameTranslationArr(index) {
      this.ingredient.translation.splice(index, 1);
    },

    addOnTechnicalInfoArr() {
      this.ingredient.technicalInfo.push({
        scientificName: "",
        graphicOriginId: "",
        suppliers: "",
        previewLabResultUrl: null,
        lab_result: "",
      });
    },

    removeTechnicalInfoArr(index) {
      this.ingredient.technicalInfo.splice(index, 1);
    },

    addOnEnergyPerSpeciesArr() {
      this.ingredient.nutritionalValues.energyPerSpecies.push({
        speciesId: "",
        digestibleEnergy: "",
        metabolizableEnergy: "",
        netEnergy: "",
      });
    },

    removeEnergyPerSpeciesArr(index) {
      this.ingredient.nutritionalValues.energyPerSpecies.splice(index, 1);
    },

    addOnSafetyInformation() {
      this.ingredient.safetyRestrictions.speciesSafety.push({
        speciesId: "",
        minInclusionRatio: "",
        maxInclusionRatio: "",
      });
    },

    convertIntoUSD(price, currency) {
      const currencyRateInUsd = this.currencyOptions.filter(
        item => item.currency === currency
      );
      const convertedAmount = price / currencyRateInUsd[0].rate;
      this.ingredient.currencyId = currency;
      this.ingredient.converted_usd_price = convertedAmount;
    },

    removeItemSafetyInformation(index) {
      this.ingredient.safetyRestrictions.speciesSafety.splice(index, 1);
    },

    onIngredientPictureChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      this.previewIngredientUrl = URL.createObjectURL(files[0]);
      if (!files.length) {
        this.dragging = false;
        return;
      }
      this.createIngredientPicture(files[0]);
    },

    async createIngredientPicture(file) {
      this.ingredient.ingredientImageUrl = file;
      this.dragging = false;
    },
    removeIngredientPicture() {
      this.ingredient.ingredientImageUrl = "";
    },

    onLabResultPictureChange(event, index) {
      console.log(event, index);
      var files = event.target.files || event.dataTransfer.files;
      this.ingredient.technicalInfo[index].previewLabResultUrl =
        URL.createObjectURL(files[0]);

      if (!files.length) {
        this.dragging = false;
        return;
      }
      this.createLabResultPicture(index, files[0]);
    },

    async createLabResultPicture(index, file) {
      this.ingredient.technicalInfo[index].lab_result = file;
      this.dragging = false;
    },
    removeLabResultPicture(index) {
      this.ingredient.technicalInfo[index].lab_result = "";
      this.ingredient.technicalInfo[index].previewLabResultUrl = "";
    },

    nutrients_color(item) {
      let nutrients_name = item.toString().toLowerCase();
      if (nutrients_name == "energy") {
        return "checkboxLabel_energy";
      } else if (nutrients_name == "protein") {
        return "checkboxLabel_protein";
      } else if (nutrients_name == "fat") {
        return "checkboxLabel_fat";
      } else if (nutrients_name == "additive") {
        return "checkboxLabel_additive";
      } else if (nutrients_name == "fibre") {
        return "checkboxLabel_fiber";
      } else if (nutrients_name == "mineral") {
        return "checkboxLabel_mineral";
      } else {
        return "";
      }
    },
  },
  mixins: [loading],
};
</script>

<style scoped lang="scss">
.v-expansion-panel-header {
  background: #f3f3f380;
  border-radius: 5px;
}

.v-expansion-panel-header--active {
  background: #fff7ec;
  border: 1px solid #ffb443;
  border-radius: 5px;
  color: #787878;
  font-size: 18px;
}

.checkboxLabel_energy {
  color: #f03737;
}

.checkboxLabel_protein {
  color: #00bd73;
}

.checkboxLabel_fat {
  color: #002287;
}

.checkboxLabel_additive {
  color: #9747ff;
}

.checkboxLabel_fiber {
  color: #e939c2;
}

.checkboxLabel_mineral {
  color: #ffb443;
}

.information-fulfilled {
  .v-expansion-panel-header,
  .v-expansion-panel-header--active {
    background: #e5f8f1;
  }
}
.name-translation-fulfilled,
.technical-info-fulfilled,
.safety-restriction-fulfilled,
.auth-permission-fulfilled,
.nutritional-value-fulfilled {
  .v-expansion-panel-header {
    background: #e5f8f1 !important;
    border: #e5f8f1 !important;
  }
}
.information-not-fulfilled {
  .v-expansion-panel-header,
  .v-expansion-panel-header--active {
    background: #f3f3f3;
  }
}

.dropZone {
  position: relative;
  padding: 30px;
  background: #ffffff;
  border: 1px dashed #d2d2d2;
  border-radius: 5px;
  text-align: center;
}

.dropZone:hover {
  /* border: 2px solid #00bd73; */
}

/* .dropZone:hover .dropZone-title {
  color: #00bd73;
} */
.dropZone-title {
  color: #787878;
}

.dropZone input {
  position: absolute;
  cursor: pointer;
  top: 0px;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
}
.dropZone-over {
  background: #5c5c5c;
  opacity: 0.8;
}

.dropZone-uploaded {
  padding: 30px;
  background: #ffffff;
  border-radius: 5px;
  position: relative;
  border: 1px dashed #eee;
}

.removeFile {
  color: red;
  margin-left: 15px;
}

.subtitle-2 {
  font-size: 14px !important;
}

.v-icon.v-icon {
  font-size: 20px;
}
</style>
